import { UserRole } from '../../auth/auth.service';
import { VehicleTypes } from '../../shared/model/vehicle-types.model';
import { TransportTypes } from '../../shared/model/transprt-types.model';
import { Services } from '../../shared/model/services.model';
import { Address } from '../../shared/model/address.model';
import { UserTraining } from 'src/app/qualifications/models/training.model';

export type Language = 'de' | 'en';

/** Values for the driver profile's `onboardingStatus` field. */
export enum DriverOnboardingStatus {
  Created = 'created',
  ProfileCompleted = 'profile_completed',
  CompanyCompleted = 'company_completed',
  DocumentsApproved = 'documents_approved',
  IdentityVerified = 'identity_verified',
  Onboarded = 'onboarded',
}

/**
 * Linear order of the onboarding states — the backend state machine only ever
 * advances forward.
 */
export const DRIVER_ONBOARDING_STATUS_ORDER: DriverOnboardingStatus[] = [
  DriverOnboardingStatus.Created,
  DriverOnboardingStatus.ProfileCompleted,
  DriverOnboardingStatus.CompanyCompleted,
  DriverOnboardingStatus.DocumentsApproved,
  DriverOnboardingStatus.IdentityVerified,
  DriverOnboardingStatus.Onboarded,
];

/**
 * Backend-provided onboarding config (`GET /drivers/profile` → `onboarding`).
 * The backend owns the role's path; the app only renders it.
 */
export interface OnboardingConfig {
  /**
   * Ordered onboarding milestones for this driver's role. Roles that skip steps
   * simply omit them (Driver has no company/documents; Manager no profile).
   */
  steps: DriverOnboardingStatus[];
}

export interface OnboardingStageProgress {
  done: boolean;
  current: boolean;
  reached: boolean;
}

export function onboardingStageProgress(
  steps: DriverOnboardingStatus[],
  currentStatus: DriverOnboardingStatus,
  targetStage: DriverOnboardingStatus,
): OnboardingStageProgress {
  const currentIndex = steps.indexOf(currentStatus);
  const targetIndex = steps.indexOf(targetStage);

  if (targetIndex < 0) {
    return { done: false, current: false, reached: false };
  }

  return {
    done: currentIndex >= targetIndex,
    current: currentIndex === targetIndex - 1,
    reached: currentIndex >= targetIndex - 1,
  };
}

export class Profile {
  constructor(
    public id: string,
    public firstName: string,
    public lastName: string,
    public dateOfBirth: Date,
    public email: string,
    public role: UserRole,
    public updatedAt: string,
    public createdAt: string,
    public language: Language,
    public phoneNumber: string,
    public drivingLicenseNumber: string,
    public drivingLicenseIssuedAt: Date,
    public mailingAddress: Address,
    public transportTypes: TransportTypes,
    public vehicleTypes: VehicleTypes,
    public services: Services,
    public premiumPartnerLevel: number,
    public isApproved: boolean,
    public salesforceId: string,
    public onboardingStatus: DriverOnboardingStatus,
    public userTrainings?: UserTraining[],
    public germanLanguageLevel?: string,
    public germanLanguageApprovedAt?: Date,
    /** Role-specific onboarding path — the app renders the stepper from this. */
    public onboarding?: OnboardingConfig,
    /**
     * Backend gate for the onboarding page (driver created on/after the release
     * date, or a legacy driver once rolled out). Onboarding is enabled by
     * default — only an explicit `false` blocks the page. Absent means enabled.
     */
    public onboardingEnabled?: boolean,
  ) {}
}
